import { helpRequestReasonLabels, helpRequestReasonSchema, type HelpRequestReason } from '@groupe-reca/contracts'
import { useQueryClient } from '@tanstack/react-query'
import { LifeBuoy, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOnline } from '@/shared/hooks/useOnline'
import { HelpOutbox, type PendingHelp } from '../../application/help/helpOutbox'
import { operationKeys, useHelpRequests } from '../../application/operationsQueries'
import { useOperator } from '../../application/OperatorServices'
import { formatTime, serverMessage } from '../../domain/fieldText'

const reasons = helpRequestReasonSchema.options

/** File locale des demandes d'aide : renvoi au retour du réseau et toutes les 30 s, même identifiant. */
function useHelpOutbox() {
  const { userId, repository } = useOperator()
  const queryClient = useQueryClient()
  const outbox = useMemo(() => new HelpOutbox(userId), [userId])
  const [pending, setPending] = useState<PendingHelp[]>(() => outbox.pending())
  const [refused, setRefused] = useState<string | null>(null)

  const flush = useCallback(async () => {
    if (outbox.pending().length === 0) return
    const result = await outbox.flush(repository)
    setPending([...outbox.pending()])
    if (result.refused[0]) setRefused(serverMessage(result.refused[0]))
    if (result.sent.length > 0) await queryClient.invalidateQueries({ queryKey: operationKeys.help(userId) })
  }, [outbox, repository, queryClient, userId])

  useEffect(() => {
    const retry = () => void flush()
    // Ce qui attend depuis une session précédente repart aussitôt, puis toutes les 30 s et au retour du réseau.
    const first = setTimeout(retry, 0)
    window.addEventListener('online', retry)
    const timer = setInterval(retry, 30_000)
    return () => {
      clearTimeout(first)
      window.removeEventListener('online', retry)
      clearInterval(timer)
    }
  }, [flush])

  const send = useCallback(
    async (request: PendingHelp) => {
      setRefused(null)
      outbox.add(request)
      setPending([...outbox.pending()])
      await flush()
    },
    [outbox, flush],
  )

  return { pending, refused, send }
}

const statusLabels = { SENT: 'Envoyée', ACKNOWLEDGED: 'Prise en charge', RESOLVED: 'Résolue' } as const

/**
 * « Besoin d'aide » : panne, retard, accès difficile, autre. « Envoyée » seulement après la réponse du serveur;
 * sinon « En attente d'envoi » (la demande reste sur la tablette et repart avec le même identifiant).
 */
export function HelpButton({ compact = false }: { compact?: boolean }) {
  const { latestPosition } = useOperator()
  const online = useOnline()
  const requests = useHelpRequests()
  const { pending, refused, send } = useHelpOutbox()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<HelpRequestReason | null>(null)
  const [comment, setComment] = useState('')
  const [touched, setTouched] = useState(false)

  const latest = requests.data?.find((request) => request.status !== 'RESOLVED') ?? null
  const label =
    pending.length > 0
      ? 'Aide en attente d’envoi'
      : latest?.status === 'ACKNOWLEDGED'
        ? 'Aide prise en charge'
        : latest?.status === 'SENT'
          ? 'Aide envoyée'
          : 'Besoin d’aide'

  async function submit() {
    setTouched(true)
    if (!reason) return
    const position = latestPosition()
    await send({
      id: crypto.randomUUID(),
      reason,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      ...(position ? { position } : {}),
      createdAt: new Date().toISOString(),
    })
    setReason(null)
    setComment('')
    setTouched(false)
  }

  return (
    <>
      <button
        type="button"
        className={`field-button field-button--outline op-help-button${pending.length > 0 ? ' is-pending' : latest ? ' is-sent' : ''}`}
        aria-label={compact ? label : undefined}
        onClick={() => {
          setOpen(true)
        }}
      >
        <LifeBuoy aria-hidden="true" /> {compact && !latest && pending.length === 0 ? 'Aide' : label}
      </button>
      {open ? (
        <div className="op-settings" role="dialog" aria-modal="true" aria-label="Besoin d’aide">
          <section className="op-settings__card op-help">
            <header className="op-settings__head">
              <h2>Besoin d’aide</h2>
              <button
                type="button"
                className="op-settings__close"
                aria-label="Fermer l’aide"
                onClick={() => {
                  setOpen(false)
                }}
              >
                <X aria-hidden="true" />
              </button>
            </header>
            <p className="op-settings__note">
              La demande part au Centre des opérations avec votre dernière position connue.
            </p>
            <div className="op-reasons" role="radiogroup" aria-label="Raison">
              {reasons.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  role="radio"
                  aria-checked={reason === candidate}
                  className={`op-reason${reason === candidate ? ' is-selected' : ''}`}
                  onClick={() => {
                    setReason(candidate)
                  }}
                >
                  {helpRequestReasonLabels[candidate]}
                </button>
              ))}
            </div>
            <label className="op-help__label" htmlFor="op-help-comment">
              Commentaire (facultatif)
            </label>
            <textarea
              id="op-help-comment"
              className="op-help__comment"
              value={comment}
              maxLength={500}
              onChange={(event) => {
                setComment(event.target.value)
              }}
            />
            {touched && !reason ? (
              <p className="field-reason__error" role="alert">
                Choisissez une raison.
              </p>
            ) : null}
            {refused ? (
              <p className="field-reason__error" role="alert">
                {refused}
              </p>
            ) : null}
            <button type="button" className="field-button field-button--primary op-help__send" onClick={() => void submit()}>
              Envoyer au bureau
            </button>

            {pending.length > 0 || (requests.data?.length ?? 0) > 0 ? (
              <ul className="op-help__list" aria-label="Mes demandes d’aide">
                {pending.map((request) => (
                  <li key={request.id}>
                    <span>
                      {helpRequestReasonLabels[request.reason]} · {formatTime(request.createdAt)}
                    </span>
                    <span className="op-chip op-chip--todo">En attente d’envoi</span>
                  </li>
                ))}
                {requests.data?.map((request) => (
                  <li key={request.id}>
                    <span>
                      {helpRequestReasonLabels[request.reason]} · {formatTime(request.createdAt)}
                      {request.status === 'ACKNOWLEDGED' && request.acknowledgedByName
                        ? ` · ${request.acknowledgedByName}`
                        : ''}
                    </span>
                    <span className={`op-chip op-chip--${request.status === 'SENT' ? 'sent' : 'done'}`}>
                      {statusLabels[request.status]}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {pending.length > 0 && !online ? (
              <p className="op-settings__note">Hors ligne : la demande partira dès le retour du réseau.</p>
            ) : null}
          </section>
        </div>
      ) : null}
    </>
  )
}
